// Third party
import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { uniqueId } from 'lodash';

// Components
import ComparisonGroupsMenuForm from './ComparisonGroupsMenuForm';
import ComparisonItemGroupLabel from './ComparisonItemGroupLabel';

// Utils
import Tooltip from '../../../components/tooltips/Tooltip';
import { formatMessage } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import ComparisonItem from '@/types/comparison-item';
import ComparisonItemGroup from '@/types/comparison-item-group';

type Props = {
  item: ComparisonItem;
  allGroupsItems: ComparisonItemGroup[];
  onRemoveFromGroup: (group: ComparisonItemGroup) => void;
  onAddToGroup: (group: ComparisonItemGroup, value: number, fee?: number) => void;
  scrollbarContainerRef?: HTMLElement;
  positionFixed?: boolean;
  menuTooltip?: string;
  menuIcon?: string;
  editGroups?: boolean;
};

type State = {
  showDropdown: boolean;
  showValuesForm: boolean;
  selectedGroup?: ComparisonItemGroup;
};

class LegacyComparisonGroupsMenu extends React.Component<Props, State> {
  id: string;

  constructor(props: Props) {
    super(props);

    this.id = uniqueId('groups-menu-toggle-');
    this.state = {
      showDropdown: false,
      showValuesForm: false,
    };
  }

  trackDropdownPosition = (dropdownRef: HTMLDivElement | any) => {
    const { positionFixed, scrollbarContainerRef } = this.props;
    const { showDropdown } = this.state;

    if (!dropdownRef || !showDropdown || !positionFixed) return;

    const containerOffsetLeft = scrollbarContainerRef?.offsetLeft || 0;
    const dropdownRefOffsetLeft = dropdownRef.getBoundingClientRect().left;

    if (dropdownRefOffsetLeft && dropdownRefOffsetLeft < containerOffsetLeft) {
      this.setShowState(false);
    }
  };

  setShowState = (isOpen: boolean) => {
    if (!isOpen) {
      this.setState({ showValuesForm: false });
    }
    return this.setState({ showDropdown: isOpen });
  };

  onAddGroup = (group: ComparisonItemGroup) => {
    this.setState({ selectedGroup: group, showValuesForm: true });
  };

  handleAddGroup = (value: number, fee?: number) => {
    if (!this.state.selectedGroup) {
      return;
    }

    this.props.onAddToGroup(this.state.selectedGroup, value, fee);
    this.setShowState(false);
  };

  handleRemoveGroup = (group: ComparisonItemGroup) => {
    this.props.onRemoveFromGroup(group);
  };

  renderGroupsSeparator = () => {
    const { item } = this.props;

    if (!item.groups || item.groups.length === 0 || this.getAvailableGroupsForItem(item).length === 0) {
      return null;
    }

    return <hr />;
  };

  getAvailableGroupsForItem = (item: ComparisonItem) => {
    if (!this.props.editGroups) {
      return [];
    }

    return this.props.allGroupsItems.filter(groupItem => !item.groups?.some(group => group.itemId === groupItem.itemId));
  };

  getItemValueFee = () => {
    const { item } = this.props;

    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return { value: 0 };
    }
    return {
      value: item.size || 0,
      ...(item.discretionaryMer && { fee: item.discretionaryMer * 100 }),
    };
  };

  renderGroupOptions = () => {
    const { item } = this.props;

    return this.getAvailableGroupsForItem(item).map((group, index) => this.renderGroup(group, index));
  };

  renderCurrentGroups = () => {
    const { item, allGroupsItems } = this.props;

    return item.groups?.map((group, index) => {
      const groupItem = allGroupsItems.find(groupItem => groupItem.itemId === group.itemId);
      return groupItem && this.renderGroup(groupItem, index, true);
    });
  };

  renderEmptyGroupsText = () => {
    if (this.props.allGroupsItems.length === 0 && !this.state.showValuesForm) {
      return formatMessage('Comparison.Groups.NoGroups');
    }
  };

  renderGroup = (group: ComparisonItemGroup, position: number, isInGroup: boolean = false) => {
    const { editGroups } = this.props;

    return (
      <Dropdown.Item key={group.itemId} role="menuitem" as="li">
        <ComparisonItemGroupLabel
          editGroups={editGroups}
          group={group}
          position={position}
          onAdd={this.onAddGroup}
          onRemove={this.handleRemoveGroup}
          isItemToAdd={!isInGroup}
        />
      </Dropdown.Item>
    );
  };

  render() {
    const { positionFixed, menuTooltip, menuIcon } = this.props;
    const { showDropdown, showValuesForm } = this.state;

    return (
      <div className="comparison-groups-menu" onClick={(event: React.MouseEvent) => event.stopPropagation()}>
        <Dropdown ref={(ref: HTMLDivElement | any) => this.trackDropdownPosition(ref)} show={showDropdown} onToggle={this.setShowState} className="menu-toggle">
          <Tooltip message={menuTooltip || 'Groups'}>
            <Dropdown.Toggle id={this.id}>
              <i className={`icon-${menuIcon}` || 'icon-plus-med'} />
            </Dropdown.Toggle>
          </Tooltip>

          <Dropdown.Menu as="ul" role="menu" popperConfig={{ strategy: positionFixed ? 'fixed' : 'absolute' }} align="end">
            {!showValuesForm && (
              <>
                {this.renderGroupOptions()}
                {this.renderGroupsSeparator()}
                {this.renderCurrentGroups()}
                {this.renderEmptyGroupsText()}
              </>
            )}

            {showValuesForm && <ComparisonGroupsMenuForm defaultValues={this.getItemValueFee()} onAddToGroup={this.handleAddGroup} />}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }
}

export default LegacyComparisonGroupsMenu;
